import React from 'react';
import { 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  CardActionArea,
  Box,
  Avatar
} from '@mui/material';
import { 
  Lock as LockIcon,
  Photo as PhotoIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const GalleryGrid = ({ galleries }) => {
  const navigate = useNavigate();

  const handleGalleryClick = (galleryId) => {
    navigate(`/gallery/${galleryId}`);
  };

  return (
    <Grid container spacing={3}>
      {galleries.map((gallery) => (
        <Grid size={{xs:12 , md:4}} key={gallery._id}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4
              }
            }}
          >
            <CardActionArea onClick={() => handleGalleryClick(gallery._id)}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={
                    gallery.coverPhoto?.thumbnailUrl || 
                    'https://placehold.co/300x200/orange/white?text=No+Photo'
                  }
                  alt={gallery.name}
                />
                
                {/* Overlay for private galleries */}
                {!gallery.isPublic && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 10, 
                      right: 10,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <LockIcon sx={{ color: 'white', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'white' }}>
                      Private
                    </Typography>
                  </Box>
                )}
                
                {/* Photo count badge */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 10, 
                    right: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <PhotoIcon sx={{ color: 'white', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    {gallery.photoCount} {gallery.photoCount === 1 ? 'photo' : 'photos'}
                  </Typography>
                </Box>
              </Box>
              
              <CardContent>
                <Typography variant="h6" component="div" noWrap>
                  {gallery.name}
                </Typography>
                
                {gallery.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 1
                    }}
                  >
                    {gallery.description}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  {/* User info */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      src={gallery.user?.profilePhotoThumbnail} 
                      alt={gallery.user?.firstName}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    >
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                      {gallery.user?.firstName} {gallery.user?.lastName}
                    </Typography>
                  </Box>
                  
                  {/* Created date */}
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(gallery.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default GalleryGrid;
